import {
  AHIcon,
  AHText,
  Box,
  Circle,
  Input,
  ListItem,
  SectionHeader,
  TouchableBox,
  useUploadImage,
 Image, PublicOrPrivateImage } from "arkh";
import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { EmployeeEditQuery } from "./__generated__/EmployeeEditQuery.graphql";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { EntityHeader } from "@/src/components/EntityHeader";
import { FormattedMessage, useIntl } from "react-intl";
import {
  createNativeStackNavigator,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import {
  MoreItem,
  useNavigationHeaderOptions,
} from "@/src/common/navigationHeaderOptions";
import { Platform, View } from "react-native";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import {
  useImageActionSheet,
  useOpenImagePicker,
} from "@/src/components/mobile/useImageActionSheet";
import { EmployeeEditUploadLinkMutation } from "./__generated__/EmployeeEditUploadLinkMutation.graphql";

import { CameraType } from "expo-image-picker";
import { EmployeeEditPhoto_employee$key } from "./__generated__/EmployeeEditPhoto_employee.graphql";
import { EmployeeEditSaveEmployeeMutation } from "./__generated__/EmployeeEditSaveEmployeeMutation.graphql";
import { ConnectedAccountCard } from "./ConnectedAccountCard";
import { EmployeeEditDeleteMutation } from "./__generated__/EmployeeEditDeleteMutation.graphql";
import { useAlert } from "@/src/common/Alert";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";

function EmployeeEditPhotoComponent(props: {
  setImage: (image: { id: string; uri: string }) => void;
  image?: React.ReactElement | null;
}) {
  const [generateUploadLink] = useMutation<EmployeeEditUploadLinkMutation>(
    graphql`
      mutation EmployeeEditUploadLinkMutation {
        uploadImageLink {
          image {
            id
          }
          url
        }
      }
    `
  );
  const image = props.image ?? <AHIcon type="camera" color="text-secondary" />;
  const { uploadImage, prepareForImageUpload } = useUploadImage();
  const onAddImage = useCallback(
    async (
      uri: string,
      { width, height }: { width: number; height: number }
    ) => {
      const uploadId = prepareForImageUpload({
        width,
        height,
        localImageURI: uri,
      });

      generateUploadLink({
        variables: {},
        onCompleted: (data) => {
          uploadId.then((uploadId) => {
            if (data.uploadImageLink) {
              uploadImage({
                imageId: data.uploadImageLink.image.id,
                uploadURL: data.uploadImageLink.url,
                uploadId,
              });
              props.setImage({
                id: data.uploadImageLink.image.id,
                uri: uri,
              });
            }
          });
        },
      });
    },
    [prepareForImageUpload, generateUploadLink, uploadImage, props]
  );
  const { openImagePicker } = useOpenImagePicker({
    onAddImage,
    options: {
      allowsEditing: true,
      aspect: [1, 1],
      cameraType: CameraType.front,
    },
  });

  const [openImageActionSheet, anchorRef] = useImageActionSheet({
    onAddImage,
    options: {
      allowsEditing: true,
      aspect: [1, 1],
      cameraType: CameraType.front,
    },
  });

  const onPress = useCallback(() => {
    if (Platform.OS === "web") {
      openImagePicker();
    } else {
      openImageActionSheet();
    }
  }, [openImageActionSheet, openImagePicker]);

  return (
    <Box style={{ alignItems: "center" }}>
      <Box spaceHorizontal="medium" spaceVertical="small">
        <TouchableBox onPress={onPress}>
          <Circle
            size="xlarge"
            color="surface-backgroundControl"
            borderColor="border-primary"
            style={{
              borderWidth: 1,
              overflow: "hidden",
            }}
          >
            {image}
          </Circle>
        </TouchableBox>
      </Box>
      <View ref={anchorRef as any}>
        <EntityHeader.Button onPress={onPress}>
          {props.image ? (
            <FormattedMessage
              defaultMessage="Edit Photo"
              description="EmployeeEdit: Edit photo button"
            />
          ) : (
            <FormattedMessage
              defaultMessage="Add Photo"
              description="EmployeeEdit: Add photo button"
            />
          )}
        </EntityHeader.Button>
      </View>
    </Box>
  );
}

function EmployeeEditPhoto(props: {
  employee: EmployeeEditPhoto_employee$key;
  setImage: (image: { id: string; uri: string }) => void;
  image: { id: string; uri: string } | null;
}) {
  const employee = useFragment(
    graphql`
      fragment EmployeeEditPhoto_employee on Employee {
        id
        image {
          ...ImagePublicOrPrivate_image
        }
      }
    `,
    props.employee
  );

  const providedImage = props.image ? (
    <Image
      uri={props.image.uri}
      type="local"
      style={{ width: "100%", height: "100%" }}
    />
  ) : null;

  const remoteImage = employee.image ? (
    <PublicOrPrivateImage
      image={employee.image}
      style={{ width: "100%", height: "100%" }}
    />
  ) : null;

  const image = providedImage ?? remoteImage;
  return <EmployeeEditPhotoComponent setImage={props.setImage} image={image} />;
}

type EmployeeEditScreenProps = {
  route: RouteProp<ParamList, "EmployeeEditMain">;
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"EmployeeEdit">,
    NativeStackNavigationProp<ParamList, "EmployeeEditMain">
  >;
};

function InnerEmployeeEdit(props: EmployeeEditScreenProps & { fetchKey: string }) {
  const data = useLazyLoadQuery<EmployeeEditQuery>(
    graphql`
      query EmployeeEditQuery($id: ID!, $storeId: ID) {
        store(id: $storeId) {
          employee(id: $id) @required(action: THROW) {
            id
            ...EmployeeEditPhoto_employee
            name
            pendingCreate
            providerId
            image {
              ...ImagePublicOrPrivate_image
            }
            storeUser {
              id
              ...ConnectedAccountCard_storeUser
            }
          }
        }
      }
    `,
    {
      id: props.route.params.id,
      storeId: props.route.params.storeId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [saveEmployee, saveEmployeeLoading] =
    useMutation<EmployeeEditSaveEmployeeMutation>(graphql`
      mutation EmployeeEditSaveEmployeeMutation(
        $input: EmployeeUpdateInput!
        $employeeId: ID!
      ) @raw_response_type {
        employee(id: $employeeId) {
          update(input: $input) {
            id
            name
            image {
              ...ImagePublicOrPrivate_image
            }
          }
        }
      }
    `);
  const employee = data.store.employee;
  const [imageUpdate, setImage] = useState<{ id: string; uri: string } | null>(
    null
  );
  const image = useMemo(() => {
    if (imageUpdate) {
      return imageUpdate;
    }
    return employee.image;
  }, [imageUpdate, employee.image]);
  const [nameUpdate, setName] = useState<string | null>(null);
  const name = useMemo(() => {
    if (nameUpdate !== null) {
      return nameUpdate;
    }
    return employee.name;
  }, [nameUpdate, employee.name]);
  const intl_tokens = useIntlTokens();
  const intl = useIntl();
  const getHeaderOptions = useNavigationHeaderOptions();

  const save = useCallback(() => {
    saveEmployee({
      onCompleted: () => {
        if (props.route.params.navigateDeprecatedToAccountCreateOnCreate) {
          props.navigation.replace("CreateAccount", {
            type: "INDIVIDUAL",
            employeeId: employee.id,
            storeId: props.route.params.storeId,
          });
        } else {
          props.navigation.goBack();
        }
      },
      variables: {
        input: {
          setImageId: imageUpdate?.id,
          setName: name,
        },
        employeeId: employee.id,
      },
      optimisticResponse: {
        employee: {
          update: {
            id: employee.id,
            name: name,
            image: {
              __typename: "PublicImage",
              __isPublicAndInternalImage: "PublicImage",
              publicImageUrl: imageUpdate?.uri,
            },
          },
        },
      },
    });
  }, [
    imageUpdate,
    saveEmployee,
    name,
    employee.id,
    props.route.params.navigateDeprecatedToAccountCreateOnCreate,
    props.route.params.storeId,
    props.navigation,
  ]);

  const [_onDelete] = useMutation<EmployeeEditDeleteMutation>(graphql`
    mutation EmployeeEditDeleteMutation($id: ID!) {
      employee(id: $id) {
        delete
      }
    }
  `);

  const onDelete = useCallback(() => {
    _onDelete({
      variables: {
        id: employee.id,
      },
      optimisticResponse: {
        employee: {
          __typename: "EmployeeMutation",
          delete: true,
        },
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
    });
  }, [employee.id, _onDelete, props.navigation]);

  const alert = useAlert();
  const openDeleteAlert = useCallback(() => {
    alert.alert(
      intl_tokens.DeleteEmployee,
      intl.formatMessage({
        defaultMessage: "Are you sure you want to delete this employee?",
        description: "EmployeeEdit: Delete employee alert message",
      }),
      [
        { text: intl_tokens.Cancel, style: "cancel" },
        { text: intl_tokens.Delete, style: "destructive", onPress: onDelete },
      ]
    );
  }, [
    alert,
    intl_tokens.DeleteEmployee,
    intl_tokens.Cancel,
    intl_tokens.Delete,
    intl,
    onDelete,
  ]);

  useLayoutEffect(() => {
    const title = intl_tokens.Employee;
    if (employee.pendingCreate) {
      props.navigation.setOptions(
        getHeaderOptions({
          type: "CREATE",
          saveLoading: saveEmployeeLoading,
          onCancel: onDelete,
          onSave: save,
          disabled: name.length === 0,
          createButtonLabel: intl_tokens.Create,
          title: intl_tokens.CreateEmployee,
        })
      );
    } else {
      const more: MoreItem[] | undefined = employee.providerId
        ? undefined
        : [
            {
              label: intl_tokens.Delete,
              icon: "trash",
              color: "negative",
              onPress: openDeleteAlert,
            },
          ];
      props.navigation.setOptions(
        imageUpdate || nameUpdate
          ? getHeaderOptions({
              type: "SAVE",
              saveLoading: saveEmployeeLoading,
              onCancel: () => {
                props.navigation.goBack();
              },
              onSave: save,
              title,
              more: more,
            })
          : getHeaderOptions({
              type: "CLOSE",
              onClose: () => {
                props.navigation.goBack();
              },
              title,
              more: more,
            })
      );
    }
  }, [
    props.navigation,
    getHeaderOptions,
    intl_tokens.Delete,
    intl_tokens.Employee,
    saveEmployee,
    saveEmployeeLoading,
    props.route.params.id,
    image,
    save,
    employee.pendingCreate,
    intl_tokens.Create,
    onDelete,
    imageUpdate,
    nameUpdate,
    employee.providerId,
    openDeleteAlert,
    intl_tokens.CreateEmployee,
    name.length,
  ]);

  return (
    <KeyboardAwareScrollView>
      <Box
        color="surface-primary"
        spaceHorizontal="medium"
        spaceVertical="large"
      >
        <Box style={{ alignItems: "center" }}>
          <Box spaceHorizontal="medium" spaceVertical="small">
            <EmployeeEditPhoto
              employee={data.store.employee}
              setImage={setImage}
              image={imageUpdate}
            />
          </Box>
        </Box>
        <SectionHeader
          right={
            employee.providerId ? (
              <>
                <AHText
                  type="subtitle"
                  color="text-secondary"
                  spaceRight="xsmall"
                >
                  HR
                </AHText>
                <AHIcon type="locked" color="text-secondary" size="small" />
              </>
            ) : null
          }
          new
        >
          <SectionHeader.Title>
            <FormattedMessage
              defaultMessage="Personal Information"
              description="EmployeeEdit: Personal information section title"
            />
          </SectionHeader.Title>
        </SectionHeader>
        {employee.providerId ? (
          <ListItem disabled isFree border="border-primary">
            <ListItem.Title>{employee.name}</ListItem.Title>
          </ListItem>
        ) : (
          <Input
            autoCapitalize="words"
            placeholder={intl_tokens.Name}
            value={name}
            onChangeText={setName}
          />
        )}
      </Box>
      {employee.storeUser ? (
        <Box space="medium">
          <SectionHeader
            left={<AHIcon type="verified-actor" color="text-secondary" />}
            new
          >
            <SectionHeader.Title>
              {intl_tokens.PersonalAccount}
            </SectionHeader.Title>
          </SectionHeader>
          <ConnectedAccountCard
            storeUser={employee.storeUser}
            onPress={() => {
              if (employee.storeUser) {
                props.navigation.navigateDeprecated("AccountEdit", {
                  id: employee.storeUser.id,
                  storeId: props.route.params.storeId,
                });
              }
            }}
          />
        </Box>
      ) : null}
    </KeyboardAwareScrollView>
  );
}

function EmployeeEditScreen(props: EmployeeEditScreenProps) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerEmployeeEdit fetchKey={fetchKey} {...props} />}
    </LazyQueryNetworkBoundrary>
  );
}

type ParamList = {
  EmployeeEditMain: {
    id: string;
    storeId?: string;
    navigateDeprecatedToAccountCreateOnCreate?: boolean;
  };
};

const Stack = createNativeStackNavigator<ParamList>();

export function EmployeeEdit(
  props: UserLoggedInNavigatorScreenProps<"EmployeeEdit">
) {
  const getHeaderOptions = useNavigationHeaderOptions();
  const intl_tokens = useIntlTokens();
  return (
    <Stack.Navigator>
      <Stack.Screen
        name="EmployeeEditMain"
        component={EmployeeEditScreen}
        initialParams={{
          id: props.route.params.id,
          storeId: props.route.params.storeId,
          navigateDeprecatedToAccountCreateOnCreate:
            props.route.params.navigateDeprecatedToAccountCreateOnCreate,
        }}
        options={({ navigation }) =>
          getHeaderOptions({
            title: intl_tokens.Employee,
            type: "CLOSE",
            onClose: () => {
              navigation.goBack();
            },
          })
        }
      />
    </Stack.Navigator>
  );
}
